<script lang="ts">
  import type {
    StickyMessage,
    StickyMessageState,
  } from '@pin/schemas/stickyMessage';
  import { format } from 'date-fns';
  import { tick } from 'svelte';
  import {
    createStickyMessage,
    deleteStickyMessage,
    editStickyMessage,
    hideStickyMessage,
    showStickyMessage,
    stickyMessageSubject$,
  } from '../../api/stickyMessage.api';
  import { adminName } from '../../stores/configStore';
  import { now } from '../../stores/now';
  import { addToast } from '../../stores/toastStore';
  import { formatElapsed } from '../../utils/elapsed';
  import BulletList from '../Icons/BulletList.svelte';
  import Link from '../Icons/Link.svelte';
  import Markdown from '../Markdown/Markdown.svelte';

  interface Props {
    stickyMessages: StickyMessageState | null;
  }
  let { stickyMessages }: Props = $props();
  let mode = $state<'list' | 'create' | 'edit'>('list');

  let editingId = $state<string | null>(null);
  let newMessage = $state('');
  let textArea: HTMLTextAreaElement | null = $state(null);

  const handleStickyMessageCreate = async () => {
    if (!newMessage.trim()) {
      addToast({ message: 'Message cannot be empty', type: 'error' });
      return;
    }
    try {
      await createStickyMessage({ text: newMessage, issuedBy: $adminName });
      stickyMessageSubject$.next();
      addToast({ message: `Sticky message created` });
      newMessage = '';
      mode = 'list';
    } catch (e) {
      addToast({
        message: `Couldn't create sticky message: ${String(e)}`,
        type: 'error',
      });
    }
  };

  const handleStickyMessageEdit = async () => {
    if (editingId == null) {
      return;
    }
    if (!newMessage.trim()) {
      addToast({ message: 'Message cannot be empty', type: 'error' });
      return;
    }
    try {
      await editStickyMessage(editingId, {
        text: newMessage,
        issuedBy: $adminName,
      });
      stickyMessageSubject$.next();
      addToast({ message: `Sticky message updated` });
      editingId = null;
      newMessage = '';
      mode = 'list';
    } catch (e) {
      addToast({
        message: `Couldn't update sticky message: ${String(e)}`,
        type: 'error',
      });
    }
  };

  const handleShowStickyMessage = async (message: StickyMessage) => {
    try {
      await showStickyMessage(message);
      stickyMessageSubject$.next();
      addToast({ message: `Sticky message shown` });
    } catch (e) {
      addToast({
        message: `Couldn't show sticky message: ${String(e)}`,
        type: 'error',
      });
    }
  };

  const handleHideStickyMessage = async () => {
    try {
      await hideStickyMessage();
      stickyMessageSubject$.next();
      addToast({
        message: `Sticky message hidden`,
      });
    } catch (e) {
      addToast({
        message: `Couldn't hide sticky message: ${String(e)}`,
        type: 'error',
      });
    }
  };

  const handleStickyMessageDelete = async (message: StickyMessage) => {
    const { id } = message;
    try {
      await deleteStickyMessage(id);
      stickyMessageSubject$.next();
      addToast({
        message: `Sticky message deleted`,
      });
    } catch (e) {
      addToast({
        message: `Couldn't delete sticky message: ${String(e)}`,
        type: 'error',
      });
    }
  };

  const wrapInMarkdown = async (
    format: 'bold' | 'italic' | 'link' | 'list'
  ) => {
    if (textArea == null) {
      return;
    }

    const start = textArea.selectionStart;
    const end = textArea.selectionEnd;
    const selected = newMessage.slice(start, end);
    let wrapped = selected;
    let cursorStart = start;
    let cursorEnd = start + selected.length;
    switch (format) {
      case 'bold':
        if (selected.length !== 0) {
          wrapped = `**${selected}**`;
          cursorStart = start;
          cursorEnd = start + wrapped.length;
        } else {
          wrapped = `****`;
          cursorStart = start + 2;
          cursorEnd = cursorStart;
        }
        break;
      case 'italic':
        if (selected.length !== 0) {
          wrapped = `*${selected}*`;
          cursorStart = start;
          cursorEnd = start + wrapped.length;
        } else {
          wrapped = `**`;
          cursorStart = start + 1;
          cursorEnd = cursorStart;
        }
        break;
      case 'link':
        if (selected.length !== 0) {
          wrapped = `[text](${selected})`;
          cursorStart = start + 1;
          cursorEnd = start + 5;
        } else {
          wrapped = `[text](url)`;
          cursorStart = start + 1;
          cursorEnd = start + 5;
        }
        break;
      case 'list':
        if (selected.length !== 0) {
          wrapped = selected
            .split('\n')
            .map((line) => (line ? `- ${line}` : '-'))
            .join('\n');
          cursorStart = start + wrapped.length;
          cursorEnd = cursorStart;
        } else {
          wrapped = '- ';
          cursorStart = start + 2;
          cursorEnd = cursorStart;
        }
        break;
    }
    newMessage = newMessage.slice(0, start) + wrapped + newMessage.slice(end);

    await tick();
    textArea.style.height = 'auto';
    textArea.style.height = `${textArea.scrollHeight}px`;
    textArea.focus();
    textArea.setSelectionRange(cursorStart, cursorEnd);
  };
</script>

<div class="container">
  {#if mode === 'create' || mode === 'edit'}
    <form
      class="create-sticky-message-container"
      onsubmit={(e) => {
        e.preventDefault();
        if (mode === 'create') {
          handleStickyMessageCreate();
        }
        if (mode === 'edit') {
          handleStickyMessageEdit();
        }
      }}
      onreset={(e) => {
        e.preventDefault();
        mode = 'list';
        newMessage = '';
        editingId = null;
      }}
    >
      <label>
        Message:
        <textarea
          rows="1"
          bind:this={textArea}
          bind:value={newMessage}
          placeholder="Message..."
          oninput={(e) => {
            const el = e.target as HTMLTextAreaElement;
            el.style.height = 'auto';
            el.style.height = el.scrollHeight + 'px';
          }}
          onkeydown={async (e) => {
            if (e.key === 'Tab' && textArea != null) {
              e.preventDefault();
              const start = textArea.selectionStart;
              const end = textArea.selectionEnd;
              newMessage =
                newMessage.slice(0, start) + '  ' + newMessage.slice(end);

              await tick();
              const cursorPosition = start + 2;
              textArea.selectionStart = cursorPosition;
              textArea.selectionEnd = cursorPosition;
            }
          }}></textarea>
      </label>
      <div class="toolbar">
        <button
          type="button"
          class="btn btn-toolbar"
          title="Make text bold"
          onclick={() => wrapInMarkdown('bold')}><strong>B</strong></button
        >
        <button
          type="button"
          class="btn btn-toolbar"
          title="Make text cursive"
          onclick={() => wrapInMarkdown('italic')}><em>I</em></button
        >
        <button
          type="button"
          class="btn btn-toolbar"
          title="Add bullet points"
          onclick={() => wrapInMarkdown('list')}><BulletList /></button
        >
        <button
          type="button"
          class="btn btn-toolbar"
          title="Add link"
          onclick={() => wrapInMarkdown('link')}><Link /></button
        >
      </div>
      <p>Preview:</p>
      <div class="message-text preview">
        <Markdown md={newMessage} />
      </div>

      <div class="create-actions">
        <button class="btn" type="submit">
          {mode === 'edit' ? 'Update' : 'Create'}
        </button>
        <button class="btn" type="reset">Cancel</button>
      </div>
    </form>
  {:else}
    <div class="header">
      <button class="btn" onclick={() => stickyMessageSubject$.next()}
        >Refresh</button
      >
      <button class="btn" onclick={() => (mode = 'create')}
        >+ New Message</button
      >
    </div>
    <div class="messages">
      {#if stickyMessages === null}
        <p>Loading...</p>
      {:else}
        {#each stickyMessages.messages as message (message.id)}
          {@const active = message.id === stickyMessages.live?.id}

          <div class="message">
            <div class="row-actions">
              {#if active}
                <button class="btn small" onclick={handleHideStickyMessage}
                  >Unpublish</button
                >
              {:else}
                <button
                  class="btn small"
                  onclick={() => handleShowStickyMessage(message)}
                  >Set active</button
                >
              {/if}
            </div>
            <details>
              <summary>
                <span class="summary-text">{message.text}</span>
                {#if active}
                  <span class="active-tag"></span>
                  {#if stickyMessages.liveAtTs != null}
                    <span
                      class="timer"
                      title="Live in the chat since {format(
                        stickyMessages.liveAtTs,
                        'HH:mm:ss'
                      )}">{formatElapsed($now - stickyMessages.liveAtTs)}</span
                    >
                  {/if}
                {/if}
              </summary>
              <div class="message-text"><Markdown md={message.text} /></div>
              <div class="sticky-message-actions">
                <button
                  type="button"
                  class="btn"
                  onclick={async () => {
                    editingId = message.id;
                    newMessage = message.text;
                    mode = 'edit';
                    await tick();
                    if (textArea != null) {
                      textArea.style.height = 'auto';
                      textArea.style.height = textArea.scrollHeight + 'px';
                    }
                  }}>Edit</button
                >
                <button
                  class="btn"
                  onclick={() => handleStickyMessageDelete(message)}
                  >Delete</button
                >
              </div>
            </details>
          </div>
        {/each}
        {#if stickyMessages.messages.length === 0}
          <p>No sticky messages yet</p>
        {/if}
      {/if}
    </div>
  {/if}
</div>

<style>
  .container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    font-size: 0.95rem;
  }

  .create-sticky-message-container {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin: 0;
    padding: 0;
    margin-top: 0.25rem;
  }

  .header {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.25rem;
  }

  .messages {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  details {
    background: var(--color-zinc-800);
    border-radius: 0.5rem;
    padding: 0.5rem 1rem;
  }

  summary {
    display: flex;
    font-weight: 600;
    cursor: pointer;
    user-select: none;
    position: relative;
    padding-right: 6rem;
  }

  .message {
    position: relative;
  }

  /* Above the summary, which is positioned for the active tag */
  .row-actions {
    position: absolute;
    z-index: 1;
    right: 0.5rem;
    top: 0.5rem;
    display: flex;
    gap: 0.25rem;
  }

  .summary-text {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .timer {
    padding-left: 0.5rem;
    font-weight: 400;
    font-size: 0.85rem;
    color: var(--color-zinc-300);
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  .active-tag {
    background: var(--color-red-500);
    height: 10px;
    width: 10px;
    border-radius: 9999px;
    position: absolute;
    left: -0.7rem;
    top: calc(0.5lh - 5px);
  }

  .sticky-message-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin: 0.5rem 0;
  }

  .message-text {
    overflow-wrap: anywhere;
    word-break: normal;
  }

  label {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    font-weight: 500;
  }

  .toolbar {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 0.25rem;
  }

  textarea {
    padding: 0.4rem;
    border: 1px solid var(--color-zinc-400);
    border-radius: 0.25rem;
    background-color: var(--color-zinc-200);
    color: var(--color-zinc-950);
    line-height: 1.4;
    resize: vertical;
    overflow: hidden;
    min-height: 2rem;
  }

  .preview {
    padding: 0.4rem;
    border: 1px solid var(--color-zinc-400);
    border-radius: 0.25rem;
    background-color: var(--color-zinc-300);
    color: var(--color-zinc-950);
    line-height: 1.4;
    min-height: 2rem;
    cursor: default;
  }

  .create-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }

  .btn {
    padding: 0.25rem 0.75rem;
    background-color: var(--color-zinc-700);
    border-radius: 0.5rem;
    color: var(--color-zinc-200);
    border: 1px solid var(--color-zinc-600);
    font-size: 1rem;
    cursor: pointer;
  }

  .btn.small {
    padding: 0.25rem 0.6rem;
    font-size: 0.9rem;
  }

  .btn:hover {
    background-color: var(--color-zinc-600);
  }

  .btn:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .btn-toolbar {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    padding: 0.25rem;
  }
</style>
